import { useState, useContext, useEffect, useMemo, useRef } from 'react'
import { useParams, useHistory, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { LeftOutlined, DownOutlined } from '@ant-design/icons'
import { Modal, Dropdown, Menu, Progress } from 'antd'
import { ClockIcon, PlusIcon, PencilAltIcon, DocumentTextIcon, UploadIcon, ExclamationCircleIcon, ExclamationIcon } from '@heroicons/react/outline'

import { PageView, Loader, Button, Notification, CarrierName } from '../../../components'
import { Filter } from '../../../components/NewTable/modules/Filter'
import { UserContext } from '../../../hooks/UserContext'
import { fetchLastmileShippingMethods } from '../../../services/lastmileManagementServices'
import { updateLastmileShippingMethod } from '../../../services'
import { fetchShippingCoveragesList } from '../../../services/shippingMethodsServices'
import { downloadCsv } from '../../../utils/csvUtils'
import { LASTMILE_SHIPPING_METHODS, DISPATCH_SCHEDULES } from '../../../navigation/constants'
import { ShippingMethodHistoryModal } from './ShippingMethodHistoryModal'
import { SingleCoverageForm } from '../../Configurations/Coberturas/createCoverages/SingleCoverageForm'
import { BulkUploadCoveragesModal } from '../../Configurations/Coberturas/createCoverages/BulkUploadCoveragesModal'
import { UpdateSingleCoverageWizard } from '../../Configurations/Coberturas/createCoverages/UpdateSingleCoverageWizard'
import { UpdateCoverageForm } from '../../Configurations/Coberturas/createCoverages/UpdateCoverageForm'
import { fetchTarifZonesStatus } from '../../../services'
import { TarifZonesStatusTab } from './ShippingCosts/TarifZonesStatusTab'
import { BulkCostWizardModal } from './ShippingCosts/BulkCostWizardModal'
import { XdockTab } from './ShippingCosts/XdockTab'
import { SingleCostForm } from './ShippingCosts/SingleCostForm'
import { EditZoneCostsModal } from './ShippingCosts/EditZoneCostsModal'
import { zoneMissingCost, zoneMissingXdock, zoneNeedsAction } from './ShippingCosts/costHelpers'

const T_PREFIX = 'lastmile_management.coverage_manager'
// Monday-first; Sunday (0) last.
const DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 0]
const TAB_KEYS = ['coverages', 'non_working_days', 'dispatch_schedules']
const COST_T_PREFIX = 'lastmile_management.shipping_costs'
// The cost views live under two tabs: "Tarifas por zona" (status + cost actions)
// and "Xdock" (Cubbo-only cross-dock management). Both read their tab label from
// the shipping_costs.tabs.* namespace.
const COST_TAB_SET = new Set(['tarifas', 'xdock'])
const COVERAGES_PER_PAGE = 25
// A Brazilian method holds ~99M coverages (one row per 8-digit CEP), too many for the list
// endpoint to count exactly inside a request. Past its cap it returns total_count/total_pages as
// a FLOOR ("at least this many") and says so via total_count_capped; only has_next_page is exact.
// So paging reads has_next_page, and a capped total is never shown as if it were the real one.
const EXPORT_PAGE_SIZE = 200
// Hard stop for the export page-walk: with only has_next_page to go on, "walk until it is false"
// would try to pull tens of millions of rows into the browser. The user is told when it trims.
const MAX_EXPORT_ROWS = 50000

// Combined "zones need action" banner shown above the tarifas table: a single
// amber card with the total plus a "N sin costo · M sin xdock" breakdown and a
// "Ver zonas" button that filters the table to those zones. Only rendered when
// there is at least one zone needing action.
const ActionBanner = ({ title, subtitle, actionLabel, clearLabel, active, onToggle }) => (
  <div className={`flex items-center justify-between gap-4 rounded-lg border border-amber-200 px-5 py-4 ${active ? 'bg-amber-100' : 'bg-amber-50'}`}>
    <div className="flex flex-col gap-1">
      <div className="inline-flex items-center gap-2.5">
        <ExclamationIcon className="h-5 w-5 flex-shrink-0 text-amber-700" />
        <span className="text-sm font-semibold text-amber-700">{title}</span>
      </div>
      <span className="pl-7 text-sm text-amber-700 opacity-80">{subtitle}</span>
    </div>
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-md bg-white px-4 py-2 text-sm font-semibold text-amber-700 shadow-sm hover:bg-amber-50"
    >
      {active ? clearLabel : actionLabel}
    </button>
  </div>
)

const INITIAL_FILTERS = {
  extended_zone: null, // null = all, true / false
  value_prefix: '',
  value_range_start: '',
  value_range_end: '',
}

// Translate the applied filter state into list-endpoint query params.
const buildCoverageFilterParams = applied => {
  const params = {}
  if (applied.extended_zone !== null) params.extended_zone = applied.extended_zone
  if (applied.value_prefix) params.value_prefix = applied.value_prefix
  if (applied.value_range_start && applied.value_range_end) {
    params.value_range_start = applied.value_range_start
    params.value_range_end = applied.value_range_end
  }
  return params
}

// Filter definitions for the shared <Filter> component. Labels/placeholders are
// i18n KEYS (the component runs them through t() itself). Zone options are empty
// here and injected once the xdock zones load. This must be component STATE (not
// a memo): <Filter> mirrors the prop into its own chip state, so the parent has
// to write the visible/value changes back for chips to render.
const INITIAL_COVERAGE_FILTERS = [
  {
    key: 'extended_zone',
    label: `${T_PREFIX}.coverages_table.filter_panel.extended_zone`,
    data: {
      visible: false,
      type: 'multiFilter',
      value: [],
      placeholder: `${T_PREFIX}.coverages_table.filter_panel.extended_zone`,
      options: [
        { id: 'yes', label: `${T_PREFIX}.coverages_table.filter_panel.extended_zone_yes` },
        { id: 'no', label: `${T_PREFIX}.coverages_table.filter_panel.extended_zone_no` },
      ],
    },
  },
  {
    key: 'value_prefix',
    label: `${T_PREFIX}.coverages_table.filter_panel.cp`,
    placeholder: `${T_PREFIX}.coverages_table.filter_panel.cp_placeholder`,
    data: { visible: false, type: 'text', value: '' },
  },
  {
    key: 'value_range_start',
    label: `${T_PREFIX}.coverages_table.filter_panel.range`,
    placeholder: `${T_PREFIX}.coverages_table.filter_panel.range_start`,
    group: 'cp_range',
    index: 0,
    data: { visible: false, type: 'text', value: '' },
  },
  {
    key: 'value_range_end',
    label: `${T_PREFIX}.coverages_table.filter_panel.range_end`,
    placeholder: `${T_PREFIX}.coverages_table.filter_panel.range_end`,
    group: 'cp_range',
    index: 1,
    data: { visible: false, type: 'text', value: '' },
  },
]

export const ShippingMethodCoverageManagerContainer = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const queryClient = useQueryClient()
  const { warehouse_id, shipping_method_id } = useParams()
  const { user } = useContext(UserContext)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('coverages')
  const [nonWorkingDays, setNonWorkingDays] = useState([])
  const [notification, setNotification] = useState({ show: false, type: '', title: '' })
  const [coveragesPage, setCoveragesPage] = useState(1)
  // One cursor per page beyond the first, so "anterior" can walk back without an OFFSET.
  // Seeking only goes forward, so the trail has to be remembered rather than recomputed.
  const [coverageCursors, setCoverageCursors] = useState([])
  const [appliedFilters, setAppliedFilters] = useState(INITIAL_FILTERS)
  const [exporting, setExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const exportCancelRef = useRef(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isUpdateOpen, setIsUpdateOpen] = useState(false)
  const [bulkUploadMode, setBulkUploadMode] = useState(null) // null | 'create' | 'update'
  const [selectedCoverageIds, setSelectedCoverageIds] = useState([])
  const [isUpdateSelectedOpen, setIsUpdateSelectedOpen] = useState(false)

  const openCreate = mode => {
    if (mode === 'bulk') setBulkUploadMode('create')
    else setIsCreateOpen(true)
  }
  const closeCreate = () => setIsCreateOpen(false)

  // "single" opens the update modal (single / range / all tabs); "bulk" opens
  // the CSV upload modal.
  const openUpdate = mode => {
    if (mode === 'bulk') {
      setBulkUploadMode('update')
      return
    }
    setIsUpdateOpen(true)
  }
  const closeUpdate = () => setIsUpdateOpen(false)

  const refetchCoverages = () => {
    queryClient.invalidateQueries(['shipping_coverages'])
    // The tarif-status zone set is the union of coverage zones and priced zones, so a coverage
    // create/import/delete that introduces or removes a zone changes it too — refresh the cost
    // tabs and the "zones needing action" banner instead of leaving them stale until a reload.
    queryClient.invalidateQueries(['tarif_zones_status'])
  }

  const canEdit = !!user?.permissions?.update_shipping_methods_management

  // Use the same source as the methods list (returns inactive methods too) so a
  // deactivated method can still be opened to view its configuration. per_page is
  // wide enough to include the target within the warehouse's methods.
  const { data: shippingMethod, isLoading } = useQuery(
    ['shipping_method', shipping_method_id, warehouse_id],
    () =>
      fetchLastmileShippingMethods({ warehouse_id, per_page: 200 }).then(res =>
        (res?.shipping_methods || []).find(m => m.id === parseInt(shipping_method_id, 10)),
      ),
    { enabled: !!warehouse_id && !!shipping_method_id },
  )

  const country = shippingMethod?.country
  // Subtitle for the bulk wizards (mirrors the coverage modal's "CUBBO · NextDay").
  const methodSubtitle = shippingMethod ? [shippingMethod.carrier_name, shippingMethod.name].filter(Boolean).join(' · ') : ''

  // ----- Shipping-costs tabs (merged into this method hub) -----
  // Full per-zone status, fetched once and shared (same query key the cost tabs
  // use) — drives the combined "zones needing action" banner.
  // Active alarm filter on the tarifas table (null | 'action').
  const [costFilter, setCostFilter] = useState(null)
  // Which BULK cost modal is open (null | 'crear' | 'actualizar').
  const [costModal, setCostModal] = useState(null)
  // Zone being edited in the per-zone "Editar costos" modal (null = closed).
  const [editZone, setEditZone] = useState(null)
  // Which SINGLE cost form modal is open (null | 'crear' | 'actualizar').
  const [singleModal, setSingleModal] = useState(null)
  const { data: costStatusData } = useQuery(['tarif_zones_status', shipping_method_id, '', false], () => fetchTarifZonesStatus(shipping_method_id), {
    enabled: !!shipping_method_id,
    refetchOnWindowFocus: false,
  })
  // Key the banner off having data, not the absence of an error: react-query keeps the last good
  // result while a background refetch fails, so it shouldn't vanish on a transient blip. Only a
  // genuine cold state (no cached data yet) hides it — distinguishing "status unknown" from "all good".
  const costStatusReady = !!costStatusData
  // Consume the backend's per-zone `missing[]` instead of re-deriving the gap rule: it
  // already applies the NONE_APPLIES / EXTENDED_ZONE skips and the mapped-but-unroutable
  // (MISSING_XDOCK_ROUTE) case, so the banner can actually reach zero and doesn't count
  // special zones as broken forever.
  const { zonesMissingCost, zonesMissingXdock } = useMemo(() => {
    const zones = costStatusData?.zones || []
    return {
      zonesMissingCost: zones.filter(zoneMissingCost),
      zonesMissingXdock: zones.filter(zoneMissingXdock),
    }
  }, [costStatusData])
  // "Is this a Cubbo Last-Mile method?" is a property of the method itself — already loaded here
  // since it was selected in the previous step — so the Xdock tab doesn't hinge on the tarif-status
  // query being ready (it stays visible even while that query loads or hits a transient error).
  // Mirrors the backend rule (carrier_name == CARRIER_NAME_CUBBO): read `internal_carrier_name` (the
  // real carrier), NOT `carrier_name`, which is the Cubbo whitelabel that also shows UBER as "CUBBO".
  const methodIsCubbo = shippingMethod?.internal_carrier_name === 'CUBBO'
  // Distinct zones needing action (any runtime-breaking gap) — the combined banner total.
  const zonesNeedingAction = useMemo(() => (costStatusData?.zones || []).filter(zoneNeedsAction), [costStatusData])

  const closeCostModal = () => setCostModal(null)

  // Entry points into the cost action flows. Creating zones/coverages lives in the
  // Coberturas tab; here "Crear costos" adds PRICES to (already-covered) zones.
  // Each Coberturas-style dropdown offers "crear/actualizar uno" (single form) and
  // "en bloque" (bulk CSV modal, which itself has the template download).
  const handleCreateSingle = () => setSingleModal('crear')
  const handleUpdateSingle = () => setSingleModal('actualizar')
  const handleCreateBulk = () => setCostModal('crear')
  const handleUpdateBulk = () => setCostModal('actualizar')

  // Coverage tabs + "Tarifas por zona"; the Xdock tab is Cubbo-only.
  const tabKeys = [...TAB_KEYS, 'tarifas', ...(methodIsCubbo ? ['xdock'] : [])]

  const tabLabel = key => {
    if (!COST_TAB_SET.has(key)) return t(`${T_PREFIX}.tabs.${key}`)
    return t(`${COST_T_PREFIX}.tabs.${key}`)
  }

  useEffect(() => {
    if (shippingMethod) {
      setNonWorkingDays(shippingMethod.non_working_days || [])
    }
  }, [shippingMethod])

  const showNotification = (type, title) => {
    setNotification({ show: true, type, title })
    setTimeout(() => setNotification({ show: false, type: '', title: '' }), 3000)
  }

  const updateMutation = useMutation(({ id, data }) => updateLastmileShippingMethod(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['shipping_method'])
      showNotification('success', t(`${T_PREFIX}.non_working_days.update_success`))
    },
    onError: error => {
      showNotification('error', error?.response?.data?.error || t(`${T_PREFIX}.non_working_days.update_error`))
    },
  })

  const toggleDay = day => {
    setNonWorkingDays(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]))
  }

  const hasDayChanges = () => {
    if (!shippingMethod) return false
    const original = [...(shippingMethod.non_working_days || [])].sort()
    const current = [...nonWorkingDays].sort()
    if (original.length !== current.length) return true
    return original.some((val, idx) => val !== current[idx])
  }

  const handleSaveDays = () => {
    if (shippingMethod) {
      updateMutation.mutate({ id: shippingMethod.id, data: { non_working_days: nonWorkingDays } })
    }
  }

  // Coverages tab data — fetched lazily when the tab is active
  const coveragesQueryParams = useMemo(
    () => ({
      shipping_method_id,
      page: coveragesPage,
      // Undefined on page 1, and dropped from the request when undefined; a backend that does
      // not understand `after` simply keeps serving the page number.
      after: coverageCursors[coveragesPage - 2],
      per_page: COVERAGES_PER_PAGE,
      ...buildCoverageFilterParams(appliedFilters),
    }),
    [shipping_method_id, coveragesPage, coverageCursors, appliedFilters],
  )

  const {
    data: coveragesData,
    isLoading: coveragesLoading,
    isFetching: coveragesFetching,
    isError: coveragesError,
  } = useQuery(['shipping_coverages', coveragesQueryParams], () => fetchShippingCoveragesList(coveragesQueryParams), {
    enabled: !!shipping_method_id && activeTab === 'coverages',
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  // Filter chip state for the shared <Filter> component. Must be state (see the
  // note on INITIAL_COVERAGE_FILTERS): the component mirrors this prop into its
  // own chip state, so we write visible/value changes back here.
  const [coverageFiltersData, setCoverageFiltersData] = useState(INITIAL_COVERAGE_FILTERS)

  // <Filter> reports one filter at a time via (filterData, key). We (1) write it
  // back into chip state so the chip renders + persists, and (2) translate it
  // into the query params the list endpoint expects.
  const handleCoverageFilterChange = (filterData, key) => {
    setCoverageFiltersData(prev => prev.map(item => (item.key === key ? { ...item, data: filterData } : item)))
    const value = filterData?.value
    setAppliedFilters(prev => {
      const next = { ...prev }
      if (key === 'extended_zone') {
        const arr = Array.isArray(value) ? value : []
        next.extended_zone = arr.length === 1 ? arr[0] === 'yes' : null
      } else {
        next[key] = value || ''
      }
      return next
    })
    setCoveragesPage(1)
    setCoverageCursors([]) // the trail belongs to the old result set
    setSelectedCoverageIds([]) // result set changes → drop the selection
  }

  // Reset visibility + values but keep the loaded options (e.g. zone list).
  const clearFilters = () => {
    setCoverageFiltersData(prev =>
      prev.map(f => ({
        ...f,
        data: { ...f.data, visible: false, value: f.data.type === 'multiFilter' ? [] : '' },
      })),
    )
    setAppliedFilters(INITIAL_FILTERS)
    setCoveragesPage(1)
    setCoverageCursors([])
    setSelectedCoverageIds([])
  }

  // Record the cursor this page ended on before advancing, so page N+1 seeks from it and
  // "anterior" can come back. Falling back to the page number keeps the buttons working against
  // a backend that does not send next_cursor.
  const goToNextCoveragePage = () => {
    // keepPreviousData leaves the previous page on screen while the next one loads, so a second
    // click before it arrives would read the SAME next_cursor again and file it under the new
    // page — silently serving page N twice. Offset paging could not get this wrong; seeking can.
    if (coveragesFetching) return
    const cursor = coveragesData?.pagination?.next_cursor
    if (cursor) {
      setCoverageCursors(prev => {
        const next = prev.slice(0, coveragesPage - 1)
        next[coveragesPage - 1] = cursor
        return next
      })
    }
    setCoveragesPage(p => p + 1)
    setSelectedCoverageIds([])
  }

  const goToPreviousCoveragePage = () => {
    if (coveragesFetching) return
    setCoveragesPage(p => Math.max(1, p - 1))
    setSelectedCoverageIds([])
  }

  // Export every coverage matching the active filters (walk all pages), to CSV.
  // A progress bar tracks the page walk; a success notification fires at the end.
  const handleExport = async () => {
    exportCancelRef.current = false
    setExporting(true)
    setExportProgress(0)
    try {
      const filterParams = buildCoverageFilterParams(appliedFilters)
      const rows = []
      let page = 1
      let cursor
      let hasMore = true
      let truncated = false
      while (hasMore) {
        if (exportCancelRef.current) return // user cancelled mid-walk
        const res = await fetchShippingCoveragesList({
          shipping_method_id,
          page,
          after: cursor,
          per_page: EXPORT_PAGE_SIZE,
          ...filterParams,
        })
        rows.push(...(res?.coverages || []))
        const pagination = res?.pagination || {}
        // Seeking keeps every page of the walk as cheap as the first. Without a cursor this falls
        // back to offset paging, which is the previous behaviour rather than a break.
        cursor = pagination.next_cursor
        // next_cursor is exact when present; otherwise lean on the count-based signals.
        // has_next_page is authoritative; total_pages is only the truth when the count is not capped.
        hasMore = cursor ? true : pagination.has_next_page ?? page < (pagination.total_pages || 1)
        // A capped total would make the percentage lie, so track progress against the export
        // ceiling instead — the bar still moves and still ends at 100%.
        const totalPages = pagination.total_count_capped ? null : pagination.total_pages
        setExportProgress(
          totalPages
            ? Math.round((page / totalPages) * 100)
            : Math.min(99, Math.round((rows.length / MAX_EXPORT_ROWS) * 100)),
        )
        if (rows.length >= MAX_EXPORT_ROWS) {
          truncated = hasMore
          break
        }
        page++
      }

      if (exportCancelRef.current) return

      if (rows.length === 0) {
        showNotification('error', t(`${T_PREFIX}.coverages_table.export_empty`))
        return
      }

      const headers = ['id', 'value', 'zone', 'extended_zone', 'enabled', 'delivery_sla_in_hours', 'delivery_sla_offset']
      const csvCell = v => {
        if (v === null || v === undefined) return ''
        let s = String(v)
        // Neutralize spreadsheet formula injection: a value starting with = + - @ executes as a
        // formula in Excel/Sheets. Prefix a single quote so it's treated as text — but skip
        // well-formed numbers so a legitimate negative like -5 isn't mangled into '-5.
        if (/^[=+\-@]/.test(s) && Number.isNaN(Number(s))) s = `'${s}`
        // Quote when the value contains a delimiter, a quote, or a newline (an unquoted newline
        // would otherwise break row alignment).
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
      }
      const body = rows.map(row => headers.map(h => csvCell(row[h])).join(','))
      const csv = [headers.join(','), ...body].join('\n')
      const ts = new Date().toISOString().slice(0, 10)
      downloadCsv(csv, `coberturas_${shipping_method_id}_${ts}.csv`)
      setExportProgress(100)
      if (truncated) {
        showNotification('warning', t(`${T_PREFIX}.coverages_table.export_truncated`, { rows: rows.length }))
      } else {
        showNotification('success', t(`${T_PREFIX}.coverages_table.export_success`))
      }
    } catch (err) {
      showNotification('error', t(`${T_PREFIX}.coverages_table.export_error`))
    } finally {
      setExporting(false)
    }
  }

  const handleCancelExport = () => {
    exportCancelRef.current = true
    setExporting(false)
    setExportProgress(0)
  }

  // ----- Row selection + bulk actions on the selected CPs -----
  const pageCoverages = coveragesData?.coverages || []
  const allPageSelected = pageCoverages.length > 0 && pageCoverages.every(c => selectedCoverageIds.includes(c.id))

  const toggleRowSelection = id => setSelectedCoverageIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))

  const toggleSelectAllOnPage = () =>
    setSelectedCoverageIds(prev => {
      const pageIds = pageCoverages.map(c => c.id)
      const everySelected = pageIds.length > 0 && pageIds.every(id => prev.includes(id))
      if (everySelected) return prev.filter(id => !pageIds.includes(id))
      return Array.from(new Set([...prev, ...pageIds]))
    })

  const clearSelection = () => setSelectedCoverageIds([])

  const onSelectionUpdated = () => {
    setSelectedCoverageIds([])
    refetchCoverages()
  }

  const listUrl = LASTMILE_SHIPPING_METHODS.replace(':warehouse_id', warehouse_id)

  const isInactive = shippingMethod && shippingMethod.is_active === false
  // Best-effort deactivation date (the method object may not carry a dedicated one).
  const deactivatedRaw = shippingMethod?.deactivated_at || shippingMethod?.disabled_at || shippingMethod?.updated_at
  const deactivatedDateObj = deactivatedRaw ? new Date(deactivatedRaw) : null
  const inactiveDate =
    isInactive && deactivatedDateObj && !Number.isNaN(deactivatedDateObj.getTime())
      ? deactivatedDateObj.toLocaleDateString(i18n.language || 'es', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : null

  // ----- Tab panel: Coverages -----
  const renderCoveragesTab = () => {
    const coverages = coveragesData?.coverages || []
    const pagination = coveragesData?.pagination || {}
    // Above the endpoint's count cap, total_count/total_pages are a floor rather than the real
    // total, so they must not be rendered as a count and must not decide when paging stops.
    const knowsTotal = pagination.total_count_capped !== true && pagination.total_pages != null
    // Exactness in order: has_next_page, then a present next_cursor, then the offset comparison
    // — which is the only one that can be wrong, because the total behind it may be a floor.
    const hasNextPage =
      pagination.has_next_page ?? (pagination.next_cursor != null || coveragesPage < (pagination.total_pages || 1))
    const showPager = hasNextPage || coveragesPage > 1 || pagination.total_pages > 1

    return (
      <div className="rounded-lg border border-gray-200 bg-white">
        {/* Toolbar: shared Filter (Filtrar dropdown + chips + Limpiar + Exportar)
                    on the left, create/update actions on the right. Note: no
                    overflow-hidden here — it would clip the filter-type dropdown. */}
        <div className="flex flex-row items-start justify-between gap-3 border-b border-gray-200 px-2 py-3">
          <Filter
            filtersData={coverageFiltersData}
            onChange={handleCoverageFilterChange}
            resetAll={clearFilters}
            hasExport
            openExportDialog={handleExport}
            exportLabel={exporting ? t(`${T_PREFIX}.coverages_table.exporting`) : t(`${T_PREFIX}.coverages_table.export`)}
            exportDisabled={exporting}
          />

          <div className="flex items-center gap-2 py-1 pr-2">
            <Dropdown
              trigger={['click']}
              overlay={
                <Menu onClick={({ key }) => openUpdate(key)}>
                  <Menu.Item key="single" icon={<PencilAltIcon className="h-4 w-4" />}>
                    {t(`${T_PREFIX}.update_modal.menu.single`)}
                  </Menu.Item>
                  <Menu.Item key="bulk" icon={<DocumentTextIcon className="h-4 w-4" />}>
                    {t(`${T_PREFIX}.update_modal.menu.bulk`)}
                  </Menu.Item>
                </Menu>
              }
            >
              <button
                type="button"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <PencilAltIcon className="h-4 w-4" />
                {t(`${T_PREFIX}.coverages_table.update_coverage`)}
                <DownOutlined className="text-xs" />
              </button>
            </Dropdown>
            <Dropdown
              trigger={['click']}
              overlay={
                <Menu onClick={({ key }) => openCreate(key)}>
                  <Menu.Item key="single" icon={<PlusIcon className="h-4 w-4" />}>
                    {t(`${T_PREFIX}.create_modal.single.title`)}
                  </Menu.Item>
                  <Menu.Item key="bulk" icon={<UploadIcon className="h-4 w-4" />}>
                    {t(`${T_PREFIX}.create_modal.bulk.title`)}
                  </Menu.Item>
                </Menu>
              }
            >
              <button
                type="button"
                className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <PlusIcon className="h-4 w-4" />
                {t(`${T_PREFIX}.coverages_table.create_coverage`)}
                <DownOutlined className="text-xs" />
              </button>
            </Dropdown>
          </div>
        </div>

        {/* Selection action bar */}
        {selectedCoverageIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 border-b border-indigo-100 bg-indigo-50 px-4 py-2.5">
            <span className="text-sm font-medium text-indigo-800">
              {t(`${T_PREFIX}.coverages_table.selection.count`, { count: selectedCoverageIds.length })}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsUpdateSelectedOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                <PencilAltIcon className="h-4 w-4" />
                {t(`${T_PREFIX}.coverages_table.selection.update`)}
              </button>
              <button type="button" onClick={clearSelection} className="text-sm font-medium text-gray-500 hover:text-gray-700">
                {t(`${T_PREFIX}.coverages_table.selection.clear`)}
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {coveragesLoading && !coveragesData ? (
          <div className="py-12 flex justify-center">
            <Loader show={true} />
          </div>
        ) : coveragesError ? (
          <div className="py-12 text-center text-sm text-red-600">{t(`${T_PREFIX}.coverages_table.load_error`)}</div>
        ) : coverages.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">{t(`${T_PREFIX}.coverages_table.empty`)}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                    <th className="w-12 px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={allPageSelected}
                        onChange={toggleSelectAllOnPage}
                      />
                    </th>
                    <th className="px-4 py-3 text-left">{t(`${T_PREFIX}.coverages_table.headers.postal_code`)}</th>
                    <th className="px-4 py-3 text-left">{t(`${T_PREFIX}.coverages_table.headers.zone`)}</th>
                    <th className="px-4 py-3 text-left">{t(`${T_PREFIX}.coverages_table.headers.extended_zone`)}</th>
                    <th className="px-4 py-3 text-left">{t(`${T_PREFIX}.coverages_table.headers.sla_hours`)}</th>
                    <th className="px-4 py-3 text-left">{t(`${T_PREFIX}.coverages_table.headers.sla_offset`)}</th>
                    <th className="px-4 py-3 text-left">{t(`${T_PREFIX}.coverages_table.headers.status`)}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm text-gray-900">
                  {coverages.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          checked={selectedCoverageIds.includes(c.id)}
                          onChange={() => toggleRowSelection(c.id)}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono">{c.value}</td>
                      <td className="px-4 py-3">{c.zone || '—'}</td>
                      <td className="px-4 py-3">
                        {c.extended_zone ? t(`${T_PREFIX}.coverages_table.boolean_yes`) : t(`${T_PREFIX}.coverages_table.boolean_no`)}
                      </td>
                      <td className="px-4 py-3">{c.delivery_sla_in_hours ?? '—'}</td>
                      <td className="px-4 py-3">{c.delivery_sla_offset ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            c.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          } ${isInactive && c.enabled ? 'opacity-50' : ''}`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${c.enabled ? 'bg-green-500' : 'bg-red-500'}`} />
                          {c.enabled ? t(`${T_PREFIX}.status_active`) : t(`${T_PREFIX}.status_inactive`)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {showPager && (
              <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
                <span>
                  {knowsTotal
                    ? `${pagination.page} / ${pagination.total_pages} · ${pagination.total_count} total`
                    : t(`${T_PREFIX}.coverages_table.page_of_many`, {
                        page: pagination.page,
                        cap: (pagination.total_count ?? 0).toLocaleString(),
                      })}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={goToPreviousCoveragePage}
                    disabled={coveragesPage <= 1 || coveragesFetching}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={goToNextCoveragePage}
                    disabled={!hasNextPage || coveragesFetching}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  // ----- Tab panel: Zonas (status + combined action banner + cost actions) -----
  // One amber banner summarizes zones needing action (missing cost or xdock) and
  // "Ver zonas" filters the table to them; the toolbar hosts Crear/Actualizar costos.
  const renderTarifasTab = () => (
    <div className="space-y-4">
      {costStatusReady && zonesNeedingAction.length > 0 && (
        <ActionBanner
          title={t(`${COST_T_PREFIX}.banners.action_title`, { count: zonesNeedingAction.length })}
          subtitle={t(`${COST_T_PREFIX}.banners.breakdown`, { cost: zonesMissingCost.length, xdock: zonesMissingXdock.length })}
          actionLabel={t(`${COST_T_PREFIX}.banners.view`)}
          clearLabel={t(`${COST_T_PREFIX}.banners.clear`)}
          active={costFilter === 'action'}
          onToggle={() => setCostFilter(f => (f === 'action' ? null : 'action'))}
        />
      )}

      <TarifZonesStatusTab
        shippingMethodId={shipping_method_id}
        country={country}
        canEdit={canEdit}
        missingFilter={costFilter}
        onCreateSingle={handleCreateSingle}
        onCreateBulk={handleCreateBulk}
        onUpdateSingle={handleUpdateSingle}
        onUpdateBulk={handleUpdateBulk}
        onEditCostsRow={setEditZone}
        t={t}
      />

      <EditZoneCostsModal zone={editZone} shippingMethodId={shipping_method_id} country={country} onClose={() => setEditZone(null)} t={t} />
    </div>
  )

  // ----- Tab panel: Non-working days -----
  const renderNonWorkingDaysTab = () => (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <p className="text-sm text-gray-600 mb-4">{t(`${T_PREFIX}.non_working_days.help`)}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {DAYS_OF_WEEK.map(day => (
          <label key={day} className={`inline-flex items-center ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
            <input
              type="checkbox"
              checked={nonWorkingDays.includes(day)}
              onChange={() => toggleDay(day)}
              disabled={!canEdit}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="ml-2 text-sm text-gray-700">{t(`lastmile_management.days.${day}`)}</span>
          </label>
        ))}
      </div>
      {canEdit && (
        <div className="mt-8 flex justify-end">
          <Button type="primary" onClick={handleSaveDays} loading={updateMutation.isLoading} disabled={!hasDayChanges()} className="px-4">
            {t(`${T_PREFIX}.non_working_days.save`)}
          </Button>
        </div>
      )}
    </div>
  )

  // ----- Tab panel: Dispatch schedules (links to the dedicated screen) -----
  const renderDispatchTab = () => (
    <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm text-center">
      <p className="text-sm text-gray-600">
        {t(`${T_PREFIX}.dispatch_link_text`)}{' '}
        <Link to={DISPATCH_SCHEDULES.replace(':warehouse_id', warehouse_id)} className="font-medium text-indigo-600 underline hover:text-indigo-700">
          {t(`${T_PREFIX}.dispatch_link_label`)}
        </Link>
      </p>
    </div>
  )

  return (
    <>
      <Notification
        show={notification.show}
        setShow={val => setNotification(prev => ({ ...prev, show: val }))}
        type={notification.type}
        title={notification.title}
      />

      {/* Export progress */}
      <Modal
        open={exporting}
        className="coverage-modal"
        title={t(`${T_PREFIX}.coverages_table.export`)}
        onCancel={handleCancelExport}
        maskClosable={false}
        width={420}
        footer={
          <div className="flex justify-end">
            <Button type="secondary" onClick={handleCancelExport}>
              {t('common.cancel')}
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-gray-600">{t(`${T_PREFIX}.coverages_table.export_in_progress`)}</p>
        <Progress percent={exportProgress} status="active" strokeColor="#4f46e5" />
      </Modal>

      <PageView
        topMenu={
          <div style={{ height: '72px' }} className="flex w-full items-center justify-between gap-4">
            <div className="flex min-w-0 flex-col leading-tight">
              {/* The arrow AND the method name together act as "back". */}
              <button
                type="button"
                onClick={() => history.push(listUrl)}
                aria-label={t(`${T_PREFIX}.back_to_methods`)}
                className="group flex min-w-0 items-center gap-2 text-left"
              >
                <LeftOutlined className="text-gray-600 transition-colors group-hover:text-gray-900" />
                <span className="truncate text-lg font-semibold text-gray-900 transition-colors group-hover:text-gray-600">
                  {shippingMethod ? (
                    <CarrierName
                      preferInternal
                      carrierName={shippingMethod.carrier_name}
                      internalCarrierName={shippingMethod.internal_carrier_name}
                      suffix={shippingMethod.shipping_name}
                      separator=" "
                    />
                  ) : (
                    t(`${T_PREFIX}.title`)
                  )}
                </span>
              </button>
              {shippingMethod && (
                <div className="mt-0.5 flex items-center gap-2 pl-6 text-sm">
                  <span className="text-gray-500">
                    ID: <span className="font-mono text-gray-700">{shippingMethod.id}</span>
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      shippingMethod.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${shippingMethod.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                    {shippingMethod.is_active ? t(`${T_PREFIX}.status_active`) : t(`${T_PREFIX}.status_inactive`)}
                  </span>
                </div>
              )}
            </div>
            {shippingMethod && (
              <button
                type="button"
                onClick={() => setIsHistoryOpen(true)}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                <ClockIcon className="h-4 w-4" />
                {t(`${T_PREFIX}.view_history`)}
              </button>
            )}
          </div>
        }
        childrenFullWidth={true}
        topMenuFullWidth={true}
      >
        {isLoading ? (
          <Loader show={true} />
        ) : !shippingMethod ? (
          <div className="max-w-2xl mx-auto p-6 text-center">
            <p className="text-gray-500">{t(`${T_PREFIX}.not_found`)}</p>
            <Link to={listUrl} className="text-indigo-600 hover:underline text-sm mt-2 inline-block">
              {t(`${T_PREFIX}.back_to_methods`)}
            </Link>
          </div>
        ) : (
          <div className="mx-auto max-w-7xl px-4 pt-3 pb-6">
            {/* Inactive-method alert — explains why active CPs still show up. */}
            {isInactive && (
              <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <p className="text-sm text-red-800">
                  {inactiveDate ? t(`${T_PREFIX}.inactive_banner_with_date`, { date: inactiveDate }) : t(`${T_PREFIX}.inactive_banner`)}
                </p>
              </div>
            )}

            {/* Tab navigation — white card, aligned with the sidebar's warehouse selector */}
            <div className="mb-4 rounded-lg border border-gray-200 bg-white shadow-sm px-4">
              <nav className="flex flex-wrap gap-x-6 gap-y-1" role="tablist" aria-label={t(`${T_PREFIX}.title`)}>
                {tabKeys.map(key => {
                  const isActive = activeTab === key
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveTab(key)}
                      className={`relative -mb-px py-3 text-sm font-medium transition-colors border-b-2 ${
                        isActive ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tabLabel(key)}
                    </button>
                  )
                })}
              </nav>
            </div>

            {/* Active tab content */}
            {activeTab === 'coverages' && renderCoveragesTab()}
            {activeTab === 'non_working_days' && renderNonWorkingDaysTab()}
            {activeTab === 'dispatch_schedules' && renderDispatchTab()}
            {activeTab === 'tarifas' && renderTarifasTab()}
            {activeTab === 'xdock' && <XdockTab country={country} canEdit={canEdit} t={t} />}
          </div>
        )}
      </PageView>

      <ShippingMethodHistoryModal open={isHistoryOpen} setOpen={setIsHistoryOpen} methodData={shippingMethod} />

      {/* Create a single coverage — opened from the "Crear cobertura ▾" dropdown. */}
      <Modal
        open={isCreateOpen}
        onCancel={closeCreate}
        className="coverage-modal"
        title={t(`${T_PREFIX}.create_modal.single.title`)}
        footer={null}
        width={760}
        destroyOnClose
      >
        <SingleCoverageForm shippingMethod={shippingMethod} onBack={closeCreate} onClose={closeCreate} onCreated={refetchCoverages} />
      </Modal>

      {/* Bulk upload by Excel — shared by "Carga masiva" (create) and
                "Varios desde Excel" (update); the mode swaps title/columns/template. */}
      <BulkUploadCoveragesModal
        open={!!bulkUploadMode}
        mode={bulkUploadMode || 'create'}
        shippingMethod={shippingMethod}
        importedBy={[user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email}
        onImported={refetchCoverages}
        onClose={() => setBulkUploadMode(null)}
      />

      {/* Update coverage — opened from the "Actualizar cobertura ▾" dropdown.
                The modal hosts the single / range / all-coverage tabs. */}
      <Modal
        open={isUpdateOpen}
        onCancel={closeUpdate}
        className="coverage-modal"
        title={t(`${T_PREFIX}.update_modal.single.title`)}
        footer={null}
        width={640}
        destroyOnClose
      >
        <UpdateSingleCoverageWizard shippingMethod={shippingMethod} onCancel={closeUpdate} onClose={closeUpdate} onUpdated={refetchCoverages} />
      </Modal>

      {/* Update only the selected CPs — opened from the selection action bar. */}
      <Modal
        open={isUpdateSelectedOpen}
        onCancel={() => setIsUpdateSelectedOpen(false)}
        className="coverage-modal"
        title={t(`${T_PREFIX}.coverages_table.selection.update`)}
        footer={null}
        width={640}
        destroyOnClose
      >
        <UpdateCoverageForm
          shippingMethod={shippingMethod}
          coverageIds={selectedCoverageIds}
          onCancel={() => setIsUpdateSelectedOpen(false)}
          onClose={() => setIsUpdateSelectedOpen(false)}
          onUpdated={onSelectionUpdated}
        />
      </Modal>

      {/* ----- Cost bulk wizards (Tarifas por zona tab): create / update costs ----- */}
      <BulkCostWizardModal
        open={costModal === 'crear'}
        mode="create"
        shippingMethodId={shipping_method_id}
        country={country}
        methodName={methodSubtitle}
        onClose={closeCostModal}
        t={t}
      />

      <BulkCostWizardModal
        open={costModal === 'actualizar'}
        mode="update"
        shippingMethodId={shipping_method_id}
        country={country}
        methodName={methodSubtitle}
        onClose={closeCostModal}
        t={t}
      />

      {/* Single-cost forms (the "crear uno / actualizar uno" dropdown option). */}
      <Modal
        open={singleModal === 'crear'}
        onCancel={() => setSingleModal(null)}
        className="cost-modal"
        title={t(`${COST_T_PREFIX}.modal.create_single`)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <SingleCostForm shippingMethodId={shipping_method_id} country={country} mode="create" onClose={() => setSingleModal(null)} t={t} />
      </Modal>

      <Modal
        open={singleModal === 'actualizar'}
        onCancel={() => setSingleModal(null)}
        className="cost-modal"
        title={t(`${COST_T_PREFIX}.modal.update_single`)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <SingleCostForm shippingMethodId={shipping_method_id} country={country} mode="update" onClose={() => setSingleModal(null)} t={t} />
      </Modal>
    </>
  )
}
